import { apiClient, type ExtendedRequestConfig } from '@/api/client'
import { AUTH, PUBLIC_TENANTS, USERS_ME, USERS_ME_AVATAR } from '@/api/endpoints'
import type { User } from '@/stores/useAuthStore'
import type {
  ApiResponseWrapper,
  ChangePasswordRequest,
  CheckSlugResponseData,
  PublicTenant,
  CompleteLoginRequest,
  ForgotPasswordRequest,
  ForgotPasswordResponseData,
  LoginRequest,
  LoginOptionsRequest,
  LoginOptionsResponseData,
  LoginResponseData,
  LoginResponseUser,
  ActiveSession,
  MeAvatarData,
  MeData,
  SetPasswordRequest,
  SignupRequest,
  SignupResponseData,
} from '@/types/api.types'
import type { UserRole } from '@/types'
import type { UpdateMePayload } from '@/types/user.types'

export interface LoginResponse {
  user: User
}

const ROLES: UserRole[] = ['staff', 'manager', 'hr', 'admin']

function mapRoleIdToRole(roleId: string): UserRole {
  const lower = roleId.toLowerCase()
  const found = ROLES.find((r) => lower.includes(`'${r}'`) || lower.includes(`slug: '${r}'`))
  return found ?? 'admin'
}

function mapLoginUserToUser(apiUser: LoginResponseUser): User {
  const name = [apiUser.firstName, apiUser.lastName].filter(Boolean).join(' ') || undefined
  return {
    id: apiUser.id,
    email: apiUser.email,
    role: mapRoleIdToRole(apiUser.roleId),
    name,
    avatar: undefined,
    organizationId: apiUser.organizationId,
    organizationSlug: apiUser.organizationSlug,
    roleId: apiUser.roleId,
    firstName: apiUser.firstName,
    lastName: apiUser.lastName,
    mustChangePassword: apiUser.mustChangePassword,
  }
}

function normalizeScope(raw: string | undefined): 'all' | 'company' | 'self' {
  if (raw === 'team') return 'company'
  if (raw === 'all' || raw === 'company' || raw === 'self') return raw
  return 'self'
}

function mapMeDataToUser(me: MeData): User {
  const name = [me.firstName, me.lastName].filter(Boolean).join(' ') || undefined
  const roleIdRaw = me.roleId
  const roleId = typeof roleIdRaw === 'string' ? roleIdRaw : roleIdRaw._id
  const roleSlug = typeof roleIdRaw === 'string' ? roleIdRaw : roleIdRaw.slug
  const roleScope =
    typeof roleIdRaw === 'string' ? undefined : roleIdRaw.scope
  return {
    id: me._id,
    email: me.email,
    role: mapRoleIdToRole(roleSlug),
    name,
    avatar: me.avatar ?? undefined,
    organizationId: me.organizationId,
    organizationSlug: me.organizationSlug ?? undefined,
    roleId,
    roleSlug,
    scope: normalizeScope(roleScope),
    compensationType: me.compensation?.compensationType,
    firstName: me.firstName,
    lastName: me.lastName,
    mustChangePassword: me.mustChangePassword,
    supportSession: me.supportSession,
  }
}

/**
 * Logs in with email, password, and organization slug.
 * Backend sets access and refresh tokens in HttpOnly cookies.
 */
export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  const { data } = await apiClient.post<ApiResponseWrapper<LoginResponseData>>(
    AUTH.LOGIN,
    credentials
  )
  const payload = data.data
  return {
    user: mapLoginUserToUser(payload.user),
  }
}

export async function getLoginOptions(
  credentials: LoginOptionsRequest
): Promise<LoginOptionsResponseData> {
  const { data } = await apiClient.post<ApiResponseWrapper<LoginOptionsResponseData>>(
    AUTH.LOGIN_OPTIONS,
    credentials
  )
  return data.data
}

export async function completeLogin(body: CompleteLoginRequest): Promise<LoginResponse> {
  const { data } = await apiClient.post<ApiResponseWrapper<LoginResponseData>>(
    AUTH.LOGIN_COMPLETE,
    body
  )
  const payload = data.data
  return {
    user: mapLoginUserToUser(payload.user),
  }
}

export interface SignupResponse {
  user: User
  organization: SignupResponseData['organization']
}

/**
 * Signs up a new organization with its owner admin account.
 * Backend provisions the org and sets auth cookies (auto-login).
 */
export async function signup(body: SignupRequest): Promise<SignupResponse> {
  const { data } = await apiClient.post<ApiResponseWrapper<SignupResponseData>>(
    AUTH.SIGNUP,
    body
  )
  const payload = data.data
  return {
    user: mapLoginUserToUser(payload.user),
    organization: payload.organization,
  }
}

/**
 * Checks whether an organization slug is valid and available.
 */
export async function checkSlug(slug: string): Promise<CheckSlugResponseData> {
  const { data } = await apiClient.get<ApiResponseWrapper<CheckSlugResponseData>>(
    AUTH.CHECK_SLUG,
    { params: { slug } }
  )
  return data.data
}

/**
 * Resolves a tenant subdomain to its name and logo. 404s for unknown or
 * inactive workspaces.
 */
export async function getPublicTenant(slug: string): Promise<PublicTenant> {
  const { data } = await apiClient.get<ApiResponseWrapper<PublicTenant>>(
    PUBLIC_TENANTS.bySlug(slug)
  )
  return data.data
}

export async function logout(): Promise<void> {
  await apiClient.post(AUTH.LOGOUT)
}

export interface GetMeOptions {
  /** When true, 401 will not trigger refresh/redirect (used for bootstrap session check). */
  isSessionCheck?: boolean
}

/**
 * Fetches the current user's full profile from GET /users/me (api/v1/users/me).
 * Response includes populated roleId with permissions for UI access control.
 */
export async function getMeProfile(options?: GetMeOptions): Promise<MeData> {
  const { data } = await apiClient.get<ApiResponseWrapper<MeData>>(USERS_ME, {
    _isSessionCheck: options?.isSessionCheck === true,
  } as ExtendedRequestConfig)
  return data.data
}

/**
 * Fetches current user profile and maps to store User shape.
 */
export async function getMe(options?: GetMeOptions): Promise<User> {
  const me = await getMeProfile(options)
  return mapMeDataToUser(me)
}

/**
 * Updates the current user's editable profile fields through PUT /users/me.
 */
export async function updateMeProfile(payload: UpdateMePayload): Promise<MeData> {
  const { data } = await apiClient.put<ApiResponseWrapper<MeData>>(USERS_ME, payload)
  return data.data
}

export async function uploadMeAvatar(file: File): Promise<MeAvatarData> {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await apiClient.post<ApiResponseWrapper<MeAvatarData>>(USERS_ME_AVATAR, formData)
  return data.data
}

export async function deleteMeAvatar(): Promise<MeAvatarData> {
  const { data } = await apiClient.delete<ApiResponseWrapper<MeAvatarData>>(USERS_ME_AVATAR)
  return data.data
}

/**
 * Updates the current user's password. Requires cookie or Bearer auth.
 */
export async function changePassword(
  body: ChangePasswordRequest
): Promise<void> {
  await apiClient.post(AUTH.CHANGE_PASSWORD, body)
}

export async function forgotPassword(
  body: ForgotPasswordRequest
): Promise<ForgotPasswordResponseData> {
  const { data } = await apiClient.post<ApiResponseWrapper<ForgotPasswordResponseData>>(
    AUTH.FORGOT_PASSWORD,
    body
  )
  return data.data
}

/**
 * Sets a password using an emailed invite or reset token.
 */
export async function setPassword(body: SetPasswordRequest): Promise<void> {
  await apiClient.post(AUTH.SET_PASSWORD, body)
}

export async function listSessions(): Promise<ActiveSession[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<ActiveSession[]>>(AUTH.SESSIONS)
  return data.data
}

export async function revokeSession(sessionId: string): Promise<void> {
  await apiClient.delete(AUTH.session(sessionId))
}

export async function signOutOtherSessions(): Promise<void> {
  await apiClient.post(AUTH.SIGN_OUT_OTHER_SESSIONS)
}

export async function forceLogoutUser(userId: string): Promise<void> {
  await apiClient.post(AUTH.forceLogout(userId))
}
